import { action, flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { getStorage, removeStorage, saveLocalStorage } from '@/utils/browsers';
import { AuthApi } from '@/apis';
import { keyStorage } from '@/configs/constants';
import { toastify } from '@/utils/toastify';
import { t } from 'i18next';
import { Profile } from '@/types/auth';
import { ResponseData } from '@/types/http';
import {
    LoginPostRequest,
    LoginPostResponse
} from '@/types/http-payload/auth';
import { FolderUser } from '@/types/files';
import { getNavigate } from '@/services/navigationService';
import GoogleAPI from '@/services/drive';

export default class AuthStore extends BaseStore {
    profile: Profile | undefined = JSON.parse(getStorage(keyStorage.PROFILE));
    token: string = getStorage(keyStorage.TOKEN);
    api: AuthApi;
    avatar: any = getStorage(keyStorage.AVATAR);
    listUsers?: FolderUser[];
    access_token?: string = getStorage(keyStorage.ACCESS_TOKEN_GOOGLE);
    sharedUrl?: string = '';
    state?: string = getStorage(keyStorage.STATE);

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            profile: observable,
            token: observable,
            avatar: observable,
            sharedUrl: observable,
            listUsers: observable,
            access_token: observable,
            clearAuthentication: action.bound,
            setAvatar: action.bound,
            setToken: action.bound,
            setProfile: action.bound,
            login: flow.bound,
            googleLogin: flow.bound,
            logout: action.bound,
            getMyInfor: flow.bound,
            setLinkRootFolder: action.bound,
            refreshTokenGG: flow.bound,
            state: observable,
            setAuthGG: action.bound
        });
        this.api = new AuthApi();
    }

    *login(payload: LoginPostRequest) {
        try {
            const res: ResponseData<LoginPostResponse> =
                yield this.rootStore.apiStore.call(this.api, this.api.login, payload);
            if (res?.ok) {
                this.token = res.data.token;
                saveLocalStorage(keyStorage.TOKEN, res.data.token);
                toastify('success', t('messages.sign_in_success'));
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *refreshTokenGG() {
        try {
            const payload = {
                state: this.state
            };
            const res: ResponseData<any> =
                yield this.rootStore.apiStore.call(this.api, this.api.refreshTokenGG, payload);
            if (res?.ok) {
                const googleAPIService = GoogleAPI.getInstance();
                googleAPIService.setAccessToken(res.data.access_token, res.data.state);
                saveLocalStorage(keyStorage.ACCESS_TOKEN_GOOGLE, res.data.access_token);
                return res.data.access_token;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    setAuthGG(accessToken: string, state: string) {
        saveLocalStorage(keyStorage.STATE, state);
        saveLocalStorage(keyStorage.ACCESS_TOKEN_GOOGLE, accessToken);
        this.access_token = accessToken;
        this.state = state;
    }

    setAvatar(avatar: string) {
        this.avatar = avatar;
    }

    setToken(token: string) {
        this.token = token;
    }

    setProfile(profile: Profile) {
        this.profile = profile;
    }

    setLinkRootFolder(folderId: string) {
        this.sharedUrl = folderId;
    }

    *googleLogin(payload: any) {
        try {
            const res: ResponseData<LoginPostResponse> = yield this.rootStore.apiStore.call(this.api, this.api.googleLogin, payload);
            if (res.ok) {
                this.token = res.data.token;
                this.profile = {
                    id: res.data.user.id,
                    name: res.data.user.name,
                    email: res.data.user.email,
                    role_id: res.data.user.role_id,
                    shared_folder_url_id: res.data.user.shared_folder_url_id,
                    company_id: res.data.user.company_id
                };
                saveLocalStorage(keyStorage.TOKEN, res.data.token);
                saveLocalStorage(keyStorage.PROFILE, JSON.stringify(this.profile!));
                saveLocalStorage(keyStorage.AVATAR, res.data.user.picture);
                toastify('success', t('messages.sign_in_success'));
                return res;
            } else {
                removeStorage([keyStorage.TOKEN, keyStorage.AVATAR, keyStorage.PROFILE]);
                this.logoutGG();
                return false;
            }
        } catch (error) {
            removeStorage([keyStorage.TOKEN, keyStorage.AVATAR, keyStorage.PROFILE]);
            this.logoutGG();
            return false;
        }
    }

    logoutGG() {
        const googleAPIService = GoogleAPI.getInstance();
        googleAPIService.signOutGG();
        removeStorage([keyStorage.STATE, keyStorage.ACCESS_TOKEN_GOOGLE]);
    }

    logout() {
        this.clearAuthentication();
    }

    *getMyInfor() {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.getMyInfor, null, { hideLoading: true, disableAlertError: true });
            if (res?.ok) {
                return res.data;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    clearAuthentication(messageError?: any) {
        this.rootStore.modalStore.hideAllModals();
        removeStorage([keyStorage.TOKEN, keyStorage.AVATAR, keyStorage.PROFILE, keyStorage.STATE, keyStorage.ACCESS_TOKEN_GOOGLE]);
        const navigate = getNavigate();
        try {
            const googleAPIService = GoogleAPI.getInstance();
            try {
                googleAPIService.signOutGG();
            } catch (error) {
            }
        } catch (error) {
            if (navigate) {
                navigate('', { replace: true });
            }
        }
        if (navigate) {
            navigate('', { replace: true });
        }
        if (messageError) {
            this.rootStore.modalStore.showErrorModal({
                content: messageError
            });
        }
    }
}
